import { AgentRunEvent } from '@lucidtech/las-sdk-browser';
import { memo, useMemo } from 'react';
import { Focusable } from 'react-aria-components';

import { merge } from '@/components';
import { Text } from '@/components/core';
import {
  CheckmarkFilledIcon,
  CloseIcon,
  PendingReviewIcon,
  UserFilledIcon,
  WaitingFilledIcon,
} from '@/components/icons';
import { User } from '@/components/ui';
import { useAnnotations } from '@/hooks';
import { toValidationTaskId, useGetValidationTask } from '@/hooks/api';
import { Annotation, DivProps, OmitChildren } from '@/utils';

import { Cell, IndentedDiv, Indicator } from './common';
import { EventTooltip, EventTooltipBodyProps } from './EventTooltip';

type ReviewBadgeProps = OmitChildren<DivProps> & {
  taskId?: string | null;
};

const IReviewBadge = ({ taskId, className, ...rest }: ReviewBadgeProps) => {
  const { data: task } = useGetValidationTask(toValidationTaskId(null, taskId));

  const { icon, style } = useMemo(() => {
    let icon;
    let style;
    switch (task?.status) {
      case 'ready':
        icon = <PendingReviewIcon variant="warning" />;
        style = 'bg-white';
        break;
      case 'in-progress':
        icon = <WaitingFilledIcon variant="warning" />;
        style = 'bg-white';
        break;
      case 'succeeded':
        icon = <CheckmarkFilledIcon variant="success" />;
        style = 'bg-white';
        break;
      case 'cancelled':
        if (task?.output?.statusReason === 'rerun') {
          icon = null;
          style = null;
        } else {
          icon = <CloseIcon className="text-white" />;
          style = 'bg-error-500 border-[1.5px] border-white';
        }
        break;
      case 'failed':
        icon = <CloseIcon className="text-white" />;
        style = 'bg-error-500 border-[1.5px] border-white';
        break;
      default:
        icon = null;
        style = null;
    }

    return { icon, style };
  }, [task]);

  if (!icon) return null;

  return (
    <div className={merge('flex size-3 items-center justify-center rounded-full', style, className)} {...rest}>
      {icon}
    </div>
  );
};

const ReviewBadge = memo(IReviewBadge) as typeof IReviewBadge;

const IReviewEventTooltipBody = ({ event }: EventTooltipBodyProps) => {
  const { data: task } = useGetValidationTask(toValidationTaskId(null, event.resourceId));
  const { forEach } = useAnnotations({
    predictionId: task?.input?.predictionId as string | undefined | null,
    documentId: task?.input?.documentId as string | undefined | null,
  });

  const isValidated = (annotation: Annotation) => {
    const hasFormatterWarnings = annotation.formatters?.some((f) => f.warnings?.length > 0);
    const hasFormatterErrors = annotation.formatters?.some((f) => f.errors?.length > 0);
    const hasValidatorWarnings = annotation.validators?.some((f) => f.warnings?.length > 0);
    const hasValidatorErrors = annotation.validators?.some((f) => f.errors?.length > 0);
    return !hasFormatterWarnings && !hasFormatterErrors && !hasValidatorWarnings && !hasValidatorErrors;
  };

  const { numValidated, numFlagged, numChanged } = useMemo(() => {
    let numChanged = 0;
    let numValidated = 0;
    let numFlagged = 0;

    forEach(({ predictionAnnotation, documentAnnotation }) => {
      if (predictionAnnotation) {
        if (isValidated(predictionAnnotation)) {
          ++numValidated;
        } else {
          ++numFlagged;
        }
      }

      if (predictionAnnotation?.value !== documentAnnotation?.value) {
        ++numChanged;
      }
    });

    return { numValidated, numFlagged, numChanged };
  }, [forEach]);

  const { content } = useMemo(() => {
    let content;
    switch (task?.status) {
      case 'succeeded':
        content = (
          <div className="gap-sm flex flex-col">
            <div className="gap-sm flex items-center">
              <Indicator variant="success" />
              <div className="rounded-xs bg-gray-700 px-1 py-0.5">
                <User userId={task?.updatedBy} props={{ img: { className: 'size-4' } }} disableTooltip showName />
              </div>
            </div>
            <div className="gap-sm flex items-center">
              <Indicator />
              <div className="flex rounded-xs bg-gray-700 px-1">
                <Text>{numChanged}</Text>
              </div>
              <Text>values were changed</Text>
            </div>
          </div>
        );
        break;
      case 'cancelled':
        if (task?.output?.statusReason === 'rerun') {
          content = null;
        } else {
          content = (
            <div className="gap-sm flex flex-col">
              <div className="gap-sm flex items-center">
                <Indicator variant="warning" />
                <div className="rounded-xs bg-gray-700 px-1 py-0.5">
                  <User userId={task?.updatedBy} props={{ img: { className: 'size-4' } }} disableTooltip showName />
                </div>
              </div>
              <Text className="ml-md">The document was rejected in review.</Text>
            </div>
          );
        }
        break;
      case 'ready':
        content = (
          <div className="gap-sm flex flex-col">
            {numValidated > 0 ? (
              <div className="gap-sm flex items-center">
                <Indicator variant="success" />
                <div className="flex rounded-xs bg-gray-700 px-1">
                  <Text>{numValidated}</Text>
                </div>
                <Text>values validated by AI</Text>
              </div>
            ) : null}
            {numFlagged > 0 ? (
              <div className="gap-sm flex items-center">
                <Indicator variant="warning" />
                <div className="flex rounded-xs bg-gray-700 px-1">
                  <Text>{numFlagged}</Text>
                </div>
                <Text>values needing review</Text>
              </div>
            ) : null}
            <IndentedDiv>
              <Text>A human needs to review AI responses before continuing.</Text>
            </IndentedDiv>
          </div>
        );
        break;
      case 'in-progress':
        content = null;
        break;
      default:
        content = null;
    }

    return { content };
  }, [numChanged, numFlagged, numValidated, task]);

  return content;
};

const ReviewEventTooltipBody = memo(IReviewEventTooltipBody) as typeof IReviewEventTooltipBody;

export type ReviewEventTooltipProps = {
  event: AgentRunEvent;
  agentId: string;
  runId: string;
};

const IReviewEventTooltip = ({ event, agentId, runId }: ReviewEventTooltipProps) => {
  const { data: task } = useGetValidationTask(toValidationTaskId(null, event.resourceId));

  const { heading } = useMemo(() => {
    let heading;
    switch (task?.status) {
      case 'succeeded':
        heading = 'Reviewed by human';
        break;
      case 'cancelled':
        heading = 'Document was rejected';
        break;
      case 'failed':
        heading = 'Review failed';
        break;
      case 'ready':
        heading = 'Pending review';
        break;
      case 'in-progress':
        heading = 'Review in progress';
        break;
      default:
        heading = 'Unknown review status';
    }

    return { heading };
  }, [task]);

  const icon = useMemo(
    () => (
      <Focusable>
        <Cell>
          <div className="relative">
            {task?.status === 'succeeded' || task?.status === 'failed' || task?.status === 'cancelled' ? (
              <>
                <User className="size-6" userId={task?.updatedBy} disableTooltip />
                <ReviewBadge
                  taskId={event.resourceId}
                  className="absolute right-0 bottom-0 z-50 translate-x-0.5 translate-y-0.5"
                />
              </>
            ) : (
              <>
                <UserFilledIcon className="size-4 text-gray-500" />
                <ReviewBadge
                  taskId={event.resourceId}
                  className="absolute right-0 bottom-0 z-50 translate-x-1.5 translate-y-1.5"
                />
              </>
            )}
          </div>
        </Cell>
      </Focusable>
    ),
    [event, task]
  );

  return (
    <EventTooltip
      event={event}
      agentId={agentId}
      runId={runId}
      heading={heading}
      Body={ReviewEventTooltipBody}
      timestamp={event.timestamp}
      icon={icon}
    />
  );
};

export const ReviewEventTooltip = memo(IReviewEventTooltip) as typeof IReviewEventTooltip;
